// Map format, validator and the starter map (spec GP-02).
// Units: integer world units, 100 per tile. Maps are content; the engine stays generic.
import { validateCity } from './city.js';
import { connected, fieldTo, gridOf, nearestOpen } from './path.js';
import { validateRace } from './race.js';
export const TILE = 100;
export const SABOTAGE_KINDS = ['nepa', 'network', 'goslow', 'fuel'];
/** Every game a stop can have. */
export const TASK_GAMES = [
    'hold',
    'wires',
    'swipe',
    'code',
    'fuel',
    'pump',
    'clamps',
    'load',
    'inspect',
    'spot',
    'sort',
    'wash',
];
/** Games other players can watch you play: a keke in sight doing one shows it (spec GP-06). */
export const OBSERVABLE_GAMES = ['wash'];
/** Every station a stop's stages use, first stage first. */
export const taskStations = (t) => [
    t.stationId,
    ...(t.stages ?? []).map((x) => x.stationId),
];
/** Two area boy posts are at least this far apart, and this far from the park and the spawns. */
export const POST_SPACING = 3 * TILE;
const t = (x, y) => ({ x: x * TILE, y: y * TILE });
/** A rectangle's four sides as wall segments (x, y, w, h in tiles). */
function block(x, y, w, h) {
    const a = t(x, y);
    const b = t(x + w, y);
    const c = t(x + w, y + h);
    const d = t(x, y + h);
    return [
        { a, b },
        { a: b, b: c },
        { a: c, b: d },
        { a: d, b: a },
    ];
}
/** The starter map: one motor park, a ring road, buildings to drive round, stops at Lagos places. */
export const LAGOS_ROUTE = {
    id: 'lagos-route',
    name: 'Lagos Route',
    width: 48 * TILE,
    height: 30 * TILE,
    walls: [
        ...block(0, 0, 48, 30), // the edge of the world
        ...block(6, 5, 8, 6), // market
        ...block(20, 4, 8, 5), // bus terminal
        ...block(34, 5, 8, 6), // filling station forecourt
        ...block(6, 19, 8, 6), // estate
        ...block(34, 19, 8, 6), // toll plaza building
        ...block(21, 21, 6, 5), // church
    ],
    stations: [
        { id: 'oshodi', kind: 'stop', label: 'Oshodi', pos: t(4, 3) },
        { id: 'mile2', kind: 'stop', label: 'Mile 2', pos: t(31, 3) },
        { id: 'cms', kind: 'stop', label: 'CMS', pos: t(45, 3) },
        { id: 'ojuelegba', kind: 'stop', label: 'Ojuelegba', pos: t(17, 13) },
        { id: 'lekki', kind: 'stop', label: 'Lekki', pos: t(45, 27) },
        { id: 'obalende', kind: 'stop', label: 'Obalende', pos: t(30, 17) },
        { id: 'berger', kind: 'stop', label: 'Berger', pos: t(3, 27) },
        { id: 'yaba', kind: 'stop', label: 'Yaba', pos: t(17, 27) },
        { id: 'ikeja', kind: 'stop', label: 'Ikeja Under Bridge', pos: t(45, 15) },
        { id: 'ojota', kind: 'stop', label: 'Ojota', pos: t(3, 21) },
        { id: 'surulere', kind: 'stop', label: 'Surulere', pos: t(10, 3) },
        { id: 'agege', kind: 'stop', label: 'Agege', pos: t(17, 5) },
        { id: 'idumota', kind: 'stop', label: 'Idumota', pos: t(31, 12) },
        { id: 'ketu', kind: 'stop', label: 'Ketu', pos: t(10, 27) },
        { id: 'park', kind: 'button', label: 'The park', pos: t(24, 15) },
        // the Task Force Yard: the clamped and the locked up, between the filling station and the toll plaza
        { id: 'yard', kind: 'yard', label: 'Task Force Yard', pos: t(38, 15) },
        // where each wahala is fixed (the park art draws them at the same spots)
        { id: 'generator', kind: 'repair', label: 'Generator', pos: { x: 540, y: 465 } },
        { id: 'mast', kind: 'repair', label: 'Mast', pos: { x: 4650, y: 840 } },
        { id: 'fuel-queue', kind: 'repair', label: 'Fuel queue', pos: { x: 3800, y: 390 } },
        { id: 'jam-west', kind: 'repair', label: 'Third Mainland west', pos: { x: 1780, y: 2755 } },
        { id: 'jam-east', kind: 'repair', label: 'Third Mainland east', pos: { x: 4420, y: 2755 } },
    ],
    spawns: [
        t(22, 13),
        t(24, 13),
        t(26, 13),
        t(22, 15),
        t(26, 15),
        t(22, 17),
        t(24, 17),
        t(26, 17),
        t(20, 14),
        t(28, 14),
        t(20, 16),
        t(28, 16),
        t(23, 12),
        t(25, 12),
        t(24, 18),
    ],
    meetingSpot: t(24, 15),
    vents: [
        { id: 'bridge-west', pos: t(2, 15), adjacent: ['bridge-east'] },
        { id: 'bridge-east', pos: t(46, 11), adjacent: ['bridge-west'] },
        { id: 'gutter-north', pos: t(17, 2), adjacent: ['gutter-south'] },
        { id: 'gutter-south', pos: t(30, 28), adjacent: ['gutter-north'] },
    ],
    // open road between the buildings, away from the stops and the park
    fixes: { nepa: ['generator'], network: ['mast'], goslow: ['jam-west', 'jam-east'], fuel: ['fuel-queue'] },
    // agberos: Oshodi, the Ojuelegba junction, Idumota by Obalende, Ikeja Under Bridge, CMS, Yaba,
    // between Ojota and Berger, by Lekki, Mile 2, Surulere, Ketu, Agege, and Razaq's bottleneck at
    // the Ojota under-bridge (the shortcut's west mouth)
    areaBoyPosts: [
        t(5, 4),
        t(17, 15),
        t(31, 14),
        t(44, 14),
        t(43, 3),
        t(18, 28),
        t(3, 24),
        t(43, 28),
        t(28, 3),
        t(10, 1.5),
        t(10, 28.5),
        t(17, 6),
        t(3, 18.5),
    ],
    razaqPost: 12,
    powerSpots: [t(17, 8), t(31, 8), t(10, 15), t(38, 12), t(17, 22), t(31, 22), t(24, 2), t(24, 28), t(3, 9), t(45, 22)],
    tasks: [
        { id: 'load-passengers', stationId: 'oshodi', label: 'Load passengers at Oshodi', kind: 'common', holdMs: 3000 },
        {
            id: 'collect-change',
            stationId: 'cms',
            label: 'Collect change at CMS',
            kind: 'short',
            holdMs: 1500,
            game: 'code',
        },
        {
            id: 'fix-wiper',
            stationId: 'ojuelegba',
            label: 'Fix wiper at Ojuelegba',
            kind: 'short',
            holdMs: 1500,
            game: 'wires',
        },
        { id: 'pay-toll', stationId: 'lekki', label: 'Pay toll at Lekki', kind: 'short', holdMs: 500, game: 'swipe' },
        { id: 'drop-passenger', stationId: 'yaba', label: 'Drop passenger for Yaba', kind: 'short', holdMs: 2000 },
        {
            id: 'collect-fare',
            stationId: 'ikeja',
            label: 'Collect fare at Ikeja Under Bridge',
            kind: 'short',
            holdMs: 2500,
        },
        { id: 'fuel-up', stationId: 'mile2', label: 'Fuel up at Mile 2', kind: 'long', holdMs: 2500, game: 'fuel' },
        {
            id: 'jump-start',
            stationId: 'obalende',
            label: 'Jump-start battery at Obalende',
            kind: 'long',
            holdMs: 2500,
            game: 'clamps',
        },
        { id: 'patch-tyre', stationId: 'berger', label: 'Patch tyre at Berger', kind: 'long', holdMs: 2000, game: 'pump' },
        { id: 'load-full', stationId: 'ojota', label: 'Load full at Ojota', kind: 'long', holdMs: 3000, game: 'load' },
        {
            id: 'check-oil',
            stationId: 'surulere',
            label: 'Drain the oil at Surulere',
            kind: 'long',
            holdMs: 1500,
            stages: [
                {
                    stationId: 'surulere',
                    label: 'Check the oil at Surulere',
                    holdMs: 1000,
                    game: 'inspect',
                    afterMs: 15_000,
                },
            ],
        },
        {
            id: 'count-fare',
            stationId: 'agege',
            label: 'Count the fare at Agege',
            kind: 'short',
            holdMs: 1500,
            game: 'sort',
        },
        {
            id: 'find-passenger',
            stationId: 'idumota',
            label: 'Find your passenger at Idumota',
            kind: 'short',
            holdMs: 1000,
            game: 'spot',
        },
        { id: 'wash-keke', stationId: 'ketu', label: 'Wash the keke at Ketu', kind: 'short', holdMs: 3000, game: 'wash' },
    ],
};
export const MAPS = { [LAGOS_ROUTE.id]: LAGOS_ROUTE };
/** A map joins MAPS when its module loads (the race ring: `@wahala/maps/third-mainland`). */
export function registerMap(m) {
    MAPS[m.id] = m;
}
export function getMap(id) {
    const m = MAPS[id];
    if (!m)
        throw new Error(`Unknown map: ${id}`);
    return m;
}
const inBounds = (m, p) => p.x > 0 && p.y > 0 && p.x < m.width && p.y < m.height;
const integral = (p) => Number.isInteger(p.x) && Number.isInteger(p.y);
/** Distance from a point to a segment (used to keep spawns and stations clear of walls). */
export function pointSegmentDistance(p, s) {
    const dx = s.b.x - s.a.x;
    const dy = s.b.y - s.a.y;
    const len2 = dx * dx + dy * dy;
    let u = len2 === 0 ? 0 : ((p.x - s.a.x) * dx + (p.y - s.a.y) * dy) / len2;
    u = Math.max(0, Math.min(1, u));
    const cx = s.a.x + u * dx - p.x;
    const cy = s.a.y + u * dy - p.y;
    return Math.sqrt(cx * cx + cy * cy);
}
/** Problems with a map. An empty list means the map is good to ship. */
export function validateMap(m, minClearance = TILE / 2) {
    const out = [];
    const ids = new Set();
    for (const s of m.stations) {
        if (ids.has(s.id))
            out.push(`duplicate station id ${s.id}`);
        ids.add(s.id);
        if (!inBounds(m, s.pos))
            out.push(`station ${s.id} is outside the map`);
        if (!integral(s.pos))
            out.push(`station ${s.id} is not on integer units`);
        if (m.walls.some((w) => pointSegmentDistance(s.pos, w) < minClearance))
            out.push(`station ${s.id} is inside or against a wall`);
    }
    // a race ring has no park: no horn, stops, stops' jobs or wahala to fix (its own checks are below)
    const isPark = !m.race && !m.city;
    if (isPark && !m.stations.some((s) => s.kind === 'button'))
        out.push('no meeting button station');
    if (isPark && m.stations.filter((s) => s.kind === 'stop').length < 6)
        out.push('fewer than 6 stops');
    m.spawns.forEach((p, i) => {
        if (!inBounds(m, p))
            out.push(`spawn ${i} is outside the map`);
        if (m.walls.some((w) => pointSegmentDistance(p, w) < minClearance))
            out.push(`spawn ${i} is inside or against a wall`);
    });
    if (isPark && m.spawns.length < 15)
        out.push('fewer than 15 spawns (one per seat)');
    const vents = new Map(m.vents.map((v) => [v.id, v]));
    for (const v of m.vents) {
        if (!inBounds(m, v.pos))
            out.push(`vent ${v.id} is outside the map`);
        for (const a of v.adjacent) {
            const other = vents.get(a);
            if (!other)
                out.push(`vent ${v.id} links to missing vent ${a}`);
            else if (!other.adjacent.includes(v.id))
                out.push(`vent link ${v.id} -> ${a} is one-way`);
        }
    }
    const taskIds = new Set();
    for (const task of m.tasks) {
        if (taskIds.has(task.id))
            out.push(`duplicate task id ${task.id}`);
        taskIds.add(task.id);
        const st = m.stations.find((s) => s.id === task.stationId);
        if (!st)
            out.push(`task ${task.id} uses missing station ${task.stationId}`);
        else if (st.kind !== 'stop')
            out.push(`task ${task.id} is on a ${st.kind} station`);
        if (!Number.isInteger(task.holdMs) || task.holdMs < 500 || task.holdMs > 10_000)
            out.push(`task ${task.id} hold time out of range`);
        if (task.game !== undefined && !TASK_GAMES.includes(task.game))
            out.push(`task ${task.id} has unknown game`);
        (task.stages ?? []).forEach((stage, i) => {
            const at = m.stations.find((x) => x.id === stage.stationId);
            if (!at)
                out.push(`task ${task.id} stage ${i + 2} uses missing station ${stage.stationId}`);
            else if (at.kind !== 'stop')
                out.push(`task ${task.id} stage ${i + 2} is on a ${at.kind} station`);
            if (!Number.isInteger(stage.holdMs) || stage.holdMs < 500 || stage.holdMs > 10_000)
                out.push(`task ${task.id} stage ${i + 2} hold time out of range`);
            const after = stage.afterMs ?? 0;
            if (!Number.isInteger(after) || after < 0 || after > 60_000)
                out.push(`task ${task.id} stage ${i + 2} wait out of range`);
            if (stage.game !== undefined && !TASK_GAMES.includes(stage.game))
                out.push(`task ${task.id} stage ${i + 2} has unknown game`);
        });
    }
    for (const kind of ['common', 'short', 'long']) {
        if (isPark && !m.tasks.some((x) => x.kind === kind))
            out.push(`no ${kind} tasks`);
    }
    for (const kind of SABOTAGE_KINDS) {
        const ids = m.fixes[kind] ?? [];
        if (isPark && ids.length === 0)
            out.push(`no repair station for ${kind}`);
        for (const id of ids)
            if (m.stations.find((st) => st.id === id)?.kind !== 'repair')
                out.push(`${kind} fix ${id} is not a repair station`);
    }
    m.powerSpots.forEach((p, i) => {
        if (!inBounds(m, p) || !integral(p))
            out.push(`power spot ${i} is outside the map or off integer units`);
        if (m.walls.some((w) => pointSegmentDistance(p, w) < minClearance))
            out.push(`power spot ${i} is inside or against a wall`);
        if (m.stations.some((st) => Math.hypot(st.pos.x - p.x, st.pos.y - p.y) < 2 * TILE))
            out.push(`power spot ${i} is on a stop`);
    });
    const posts = m.areaBoyPosts ?? [];
    if (m.areaBoyPosts && posts.length < 2)
        out.push('fewer than 2 area boy posts (a boy needs somewhere to move)');
    const park = m.stations.find((st) => st.kind === 'button');
    const far = (a, b) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2 >= POST_SPACING * POST_SPACING;
    if (m.razaqPost !== undefined && !posts[m.razaqPost])
        out.push('razaqPost names no area boy post');
    if (m.stations.filter((st) => st.kind === 'yard').length > 1)
        out.push('more than one Task Force Yard');
    posts.forEach((p, i) => {
        if (!inBounds(m, p) || !integral(p))
            out.push(`area boy post ${i} is outside the map or off integer units`);
        if (m.walls.some((w) => pointSegmentDistance(p, w) < minClearance))
            out.push(`area boy post ${i} is inside or against a wall`);
        if (posts.some((q, j) => j < i && !far(p, q)))
            out.push(`area boy post ${i} is too close to another post`);
        if (park && !far(p, park.pos))
            out.push(`area boy post ${i} is too close to the park`);
        if (m.spawns.some((x) => !far(p, x)))
            out.push(`area boy post ${i} is too close to a spawn`);
        if (m.vents.some((v) => !far(p, v.pos)))
            out.push(`area boy post ${i} is on a shortcut`);
    });
    for (const w of m.walls) {
        if (!integral(w.a) || !integral(w.b))
            out.push('wall not on integer units');
    }
    if (m.city)
        out.push(...validateCity(m));
    if (m.race)
        out.push(...validateRace(m, (a, b) => connected(m, a, b), 
        // the walk in the path grid's steps, as @wahala/maps/walk counts it (not imported here, so
        // the web shell, which carries this module, never carries the race's walk)
        (a, b) => {
            const g = gridOf(m);
            const from = nearestOpen(g, a);
            const to = nearestOpen(g, b);
            const steps = from === null || to === null ? -1 : fieldTo(m, g, to)[from];
            return steps < 0 ? -1 : steps * TILE;
        }));
    return out;
}
export * from './city.js';
export * from './path.js';
export * from './race.js';
export * from './tiled.js';
//# sourceMappingURL=index.js.map